/**
 * Performance logging utility for middleware instrumentation
 * Only logs when LOG_PROXY_MIDDLEWARE_PERFORMANCE=true (staging/review apps)
 *
 * Log levels (numeric):
 * - 10: trace - Very detailed debugging
 * - 20: debug - Debugging information
 * - 30: info - Informational messages (default)
 * - 40: warn - Warnings
 * - 50: error - Errors
 * - 60: fatal - Fatal errors
 */

import { logger } from './logger';

const SHOULD_LOG = process.env['LOG_PROXY_MIDDLEWARE_PERFORMANCE'] === 'true';

export interface PerformanceMetrics<T> {
  component: string;
  operation: string;
  duration_ms: number;
  details?: T;
}

/**
 * Log performance metrics as structured JSON
 * Only logs when LOG_PROXY_MIDDLEWARE_PERFORMANCE environment variable is enabled
 *
 * @param metrics - Performance metrics to log
 *
 * @example
 * ```typescript
 * logPerformance({
 *   component: 'middleware',
 *   operation: 'jwt_parse',
 *   duration_ms: 2.5,
 *   details: { hasToken: true, isStale: false }
 * });
 * ```
 */
export function logPerformance<T>(metrics: PerformanceMetrics<T>): void {
  if (!SHOULD_LOG) {
    return;
  }

  logger.info(
    {
      type: 'performance',
      ...metrics,
    },
    `[Performance] ${metrics.component}.${metrics.operation}: ${metrics.duration_ms.toFixed(2)}ms`,
  );
}

/**
 * Measure execution time of an async function
 *
 * @param fn - Async function to measure
 * @returns Tuple of [result, duration_ms]
 *
 * @example
 * ```typescript
 * const [data, duration] = await measureAsync(() => fetchData());
 * logPerformance({
 *   component: 'api',
 *   operation: 'fetch',
 *   duration_ms: duration
 * });
 * ```
 */
export async function measureAsync<T>(fn: () => Promise<T>): Promise<[T, number]> {
  const start = performance.now();
  const result = await fn();
  const duration = performance.now() - start;

  return [result, duration];
}

/**
 * Measure execution time of a synchronous function
 *
 * @param fn - Sync function to measure
 * @returns Tuple of [result, duration_ms]
 *
 * @example
 * ```typescript
 * const [device, duration] = measureSync(() => detectDevice(req));
 * logPerformance({
 *   component: 'middleware',
 *   operation: 'device_detection',
 *   duration_ms: duration
 * });
 * ```
 */
export function measureSync<T>(fn: () => T): [T, number] {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;

  return [result, duration];
}
